import React from 'react';
import { ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  KpiCard, ChartCard, ExportMenu, ScreenHeader, EmptyMeter, DonutCenter, AXIS_TICK, CHART_FILL,
} from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { resolveDoctorName } from '@/lib/ops-data.js';
import { daysBetween, fmtDayShort, fmtNum, fmtCompact } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';
import { LoadingBody, ErrorBody } from './Overview.jsx';

// Harness ops — the agent/AI-run operational view over raw telemetry (§4):
// runs/day per kind, avg steps + tool calls, latency p50/p95, outcome stacked
// bars, model mix donut, and a sortable client-paginated drill-down table of
// recent events.
const KINDS = ['stt', 'tts', 'llm', 'harness', 'agent-turn', 'realtime'];
const ALL = '__all__';
const PAGE_SIZE = 25;
const OUTCOMES = ['ok', 'error', 'timeout'];
const OUTCOME_COLOR = { ok: 'hsl(var(--chart-2))', error: 'hsl(var(--chart-5))', timeout: 'hsl(var(--chart-3))' };

function pctl(sorted, p) {
  if (!sorted.length) return null;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[idx];
}

export default function HarnessOps({ range }) {
  const ops = useOps();
  const [kind, setKind] = React.useState(ALL);
  const [sortKey, setSortKey] = React.useState('at');
  const [sortDir, setSortDir] = React.useState('desc');
  const [page, setPage] = React.useState(0);

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;

  const tel = ops.telemetry;
  if (!tel.length) return <div className="p-6"><ScreenHeader title="Harness ops" /><EmptyMeter sub="Run telemetry appears once ops-telemetry events flow (OPS_* env set on an instance)." /></div>;

  const rows = kind === ALL ? tel : tel.filter((t) => t.kind === kind);

  // KPIs.
  const latencies = rows.map((t) => Number(t.latencyMs)).filter(Number.isFinite).sort((a, b) => a - b);
  const p50 = pctl(latencies, 50);
  const p95 = pctl(latencies, 95);
  const stepsRows = rows.filter((t) => Number.isFinite(Number(t.steps)));
  const avgSteps = stepsRows.length ? stepsRows.reduce((s, t) => s + Number(t.steps), 0) / stepsRows.length : null;
  const toolRows = rows.filter((t) => Number.isFinite(Number(t.toolCalls)));
  const avgTools = toolRows.length ? toolRows.reduce((s, t) => s + Number(t.toolCalls), 0) / toolRows.length : null;
  const errRate = rows.length ? rows.filter((t) => t.outcome !== 'ok').length / rows.length * 100 : null;

  // Runs/day per kind (stacked) + outcomes/day (stacked).
  const days = daysBetween(range.from, range.to);
  const activeKinds = KINDS.filter((k) => tel.some((t) => t.kind === k));
  const runsPerDay = days.map((d) => {
    const r = { label: fmtDayShort(d) };
    for (const k of activeKinds) r[k] = 0;
    for (const t of rows) if ((t.at || '').slice(0, 10) === d) r[t.kind] = (r[t.kind] || 0) + 1;
    return r;
  });
  const outcomesPerDay = days.map((d) => {
    const r = { label: fmtDayShort(d), ok: 0, error: 0, timeout: 0 };
    for (const t of rows) if ((t.at || '').slice(0, 10) === d) r[t.outcome] = (r[t.outcome] || 0) + 1;
    return r;
  });

  // Model mix donut.
  const modelCounts = new Map();
  for (const t of rows) {
    const key = `${t.provider}/${t.product}`;
    modelCounts.set(key, (modelCounts.get(key) || 0) + 1);
  }
  const modelSegs = [...modelCounts.entries()].sort((a, b) => b[1] - a[1]).map(([k, v], i) => ({
    key: k, label: k, value: v, fill: CHART_FILL(i),
  }));

  // Sortable, paginated event table.
  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey]; const bv = b[sortKey];
    const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageClamped = Math.min(page, pages - 1);
  const visible = sorted.slice(pageClamped * PAGE_SIZE, (pageClamped + 1) * PAGE_SIZE);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
    setPage(0);
  };

  const csv = () => downloadCsv(
    `ops-harness-${range.from}_${range.to}.csv`,
    [
      { key: 'at', label: 'At' }, { key: 'kind', label: 'Kind' },
      { key: 'hospital', label: 'Hospital' }, { key: 'provider', label: 'Provider' },
      { key: 'product', label: 'Product' }, { key: 'usecase', label: 'Use case' },
      { key: 'doctor', label: 'Doctor' }, { key: 'steps', label: 'Steps' },
      { key: 'toolCalls', label: 'Tool calls' }, { key: 'latencyMs', label: 'Latency ms' },
      { key: 'outcome', label: 'Outcome' },
    ],
    sorted.map((t) => ({ ...t, doctor: resolveDoctorName(t.doctor) })),
  );
  const txt = () => downloadTxtReport(`ops-harness-${range.from}_${range.to}.txt`, {
    title: 'Healther Ops — Harness ops',
    meta: [
      `Range: ${range.from} → ${range.to}`,
      `Kind: ${kind === ALL ? 'all' : kind}`,
      `Runs: ${rows.length}`,
      `Latency p50 / p95: ${p50 ?? '—'} / ${p95 ?? '—'} ms`,
      `Avg steps: ${avgSteps !== null ? avgSteps.toFixed(1) : '—'} · avg tool calls: ${avgTools !== null ? avgTools.toFixed(1) : '—'}`,
      `Non-ok rate: ${errRate !== null ? errRate.toFixed(1) + '%' : '—'}`,
    ],
    sections: [{
      heading: 'Model mix',
      columns: [{ key: 'label', label: 'Provider/model' }, { key: 'value', label: 'Runs' }],
      rows: modelSegs,
    }],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="Harness ops" subtitle="Agent & AI-run operations — volume, latency, outcomes, model mix">
        <Select value={kind} onValueChange={(v) => { setKind(v); setPage(0); }}>
          <SelectTrigger size="sm" className="min-w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All kinds</SelectItem>
            {activeKinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label="Runs" value={fmtNum(rows.length)} sub={`${(rows.length / Math.max(1, days.length)).toFixed(1)} / day avg`} />
        <KpiCard label="Latency p50 / p95"
          value={p50 !== null ? `${fmtCompact(p50)} / ${fmtCompact(p95)} ms` : '—'}
          sub="end-to-end run latency" />
        <KpiCard label="Avg steps · tool calls"
          value={avgSteps !== null ? `${avgSteps.toFixed(1)} · ${avgTools !== null ? avgTools.toFixed(1) : '—'}` : '—'}
          sub={`${fmtNum(stepsRows.length)} agentic runs`} />
        <KpiCard label="Non-ok rate"
          tone={errRate !== null && errRate > 8 ? 'text-[hsl(var(--allergy))]' : undefined}
          value={errRate !== null ? `${errRate.toFixed(1)}%` : '—'} sub="errors + timeouts" />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <ChartCard title="Runs per day" subtitle="Stacked by kind" className="lg:col-span-2"
          empty={rows.length === 0}>
          <ChartContainer
            config={Object.fromEntries(activeKinds.map((k, i) => [k, { label: k, color: CHART_FILL(i) }]))}
            className="h-[210px] w-full"
          >
            <BarChart data={runsPerDay} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} minTickGap={24} />
              <YAxis width={34} tickLine={false} axisLine={false} tick={AXIS_TICK} allowDecimals={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              {activeKinds.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="runs" fill={CHART_FILL(i)}
                  radius={i === activeKinds.length - 1 ? [3, 3, 0, 0] : 0} />
              ))}
            </BarChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Model mix" subtitle="Share of runs by provider/model" empty={modelSegs.length === 0}>
          <div className="relative">
            <ChartContainer
              config={Object.fromEntries(modelSegs.map((s) => [s.key, { label: s.label, color: s.fill }]))}
              className="mx-auto h-[210px] w-full"
            >
              <PieChart>
                <ChartTooltip cursor={false} content={<ChartTooltipContent nameKey="label" hideLabel />} />
                <Pie data={modelSegs} dataKey="value" nameKey="label" innerRadius={50} outerRadius={76} strokeWidth={2} paddingAngle={2}>
                  {modelSegs.map((s) => <Cell key={s.key} fill={s.fill} />)}
                </Pie>
              </PieChart>
            </ChartContainer>
            <DonutCenter primary={fmtNum(rows.length)} secondary="runs" />
          </div>
        </ChartCard>
      </div>

      <div className="mt-3">
        <ChartCard title="Outcomes per day" subtitle="ok vs error vs timeout" empty={rows.length === 0}>
          <ChartContainer
            config={Object.fromEntries(OUTCOMES.map((o) => [o, { label: o, color: OUTCOME_COLOR[o] }]))}
            className="h-[170px] w-full"
          >
            <BarChart data={outcomesPerDay} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} minTickGap={24} />
              <YAxis width={34} tickLine={false} axisLine={false} tick={AXIS_TICK} allowDecimals={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              {OUTCOMES.map((o, i) => (
                <Bar key={o} dataKey={o} stackId="out" fill={OUTCOME_COLOR[o]}
                  radius={i === OUTCOMES.length - 1 ? [3, 3, 0, 0] : 0} />
              ))}
            </BarChart>
          </ChartContainer>
        </ChartCard>
      </div>

      <Card className="mt-4 gap-0 overflow-hidden py-0">
        <CardHeader className="flex-row items-center border-b py-4">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-[13.5px]">Recent telemetry events</CardTitle>
            <CardDescription className="text-[11.5px]">Click a column to sort · {fmtNum(sorted.length)} events</CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="icon-xs" disabled={pageClamped === 0} onClick={() => setPage(pageClamped - 1)}>
              <ChevronLeft className="size-3" />
            </Button>
            <span className="text-[11.5px] tabular-nums text-muted-foreground">{pageClamped + 1} / {pages}</span>
            <Button variant="outline" size="icon-xs" disabled={pageClamped >= pages - 1} onClick={() => setPage(pageClamped + 1)}>
              <ChevronRight className="size-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    ['at', 'At'], ['kind', 'Kind'], ['hospital', 'Hospital'],
                    ['provider', 'Provider'], ['product', 'Model'], ['usecase', 'Use case'],
                    ['doctor', 'Doctor'], ['steps', 'Steps'], ['toolCalls', 'Tools'],
                    ['latencyMs', 'Latency'], ['outcome', 'Outcome'],
                  ].map(([k, label], i) => (
                    <TableHead key={k} className={i === 0 ? 'pl-4' : ''}>
                      <button type="button" onClick={() => setSort(k)}
                        className="inline-flex items-center gap-1 hover:text-foreground">
                        {label}
                        <ArrowUpDown className={`size-3 ${sortKey === k ? 'text-foreground' : 'opacity-40'}`} />
                      </button>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="pl-4 tabular-nums text-[11.5px] text-muted-foreground">
                      {(t.at || '').replace('T', ' ').slice(0, 16)}
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{t.kind}</Badge></TableCell>
                    <TableCell className="text-[12px]">{t.hospital}</TableCell>
                    <TableCell className="text-[12px]">{t.provider}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">{t.product}</TableCell>
                    <TableCell className="text-[12px] text-muted-foreground">{t.usecase}</TableCell>
                    <TableCell className="text-[12px] text-muted-foreground">{resolveDoctorName(t.doctor)}</TableCell>
                    <TableCell className="tabular-nums text-[12px]">{t.steps ?? '—'}</TableCell>
                    <TableCell className="tabular-nums text-[12px]">{t.toolCalls ?? '—'}</TableCell>
                    <TableCell className="tabular-nums text-[12px]">{t.latencyMs ? `${fmtNum(t.latencyMs)} ms` : '—'}</TableCell>
                    <TableCell>
                      <span className={`text-[11.5px] font-semibold ${t.outcome === 'ok' ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>
                        {t.outcome}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
